import { supabase } from "@/lib/supabase";

/** Logical bucket that uploaded media is written to. */
export const MEDIA_BUCKET = "public";

/** Storage directory prefix for uploads (must be a valid folder segment). */
const UPLOAD_DIR = "uploads";

/** 25 MB per-file ceiling keeps the browser responsive during upload. */
const MAX_KB = 25000;

const IMAGE_EXT = ["jpg", "jpeg", "png", "webp", "gif", "avif"];
const VIDEO_EXT = ["mp4", "webm", "mov", "m4v"];

export type AssetType = "image" | "video" | "document";

export function extensionOf(name: string): string {
  const parts = name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
}

export function assetTypeFor(file: File): AssetType {
  const ext = extensionOf(file.name);
  if (file.type.startsWith("image/") || IMAGE_EXT.includes(ext)) return "image";
  if (file.type.startsWith("video/") || VIDEO_EXT.includes(ext)) return "video";
  return "document";
}

/**
 * Readdy Backend Storage requires file names to be 1-63 lowercase characters that
 * start with a letter or number and otherwise contain only letters, numbers,
 * dots, underscores or hyphens. Returns an error message when invalid.
 */
export function validateFileName(name: string): string | null {
  if (!name) return "This file has no name.";
  if (name.length > 63) return "File names must be 63 characters or fewer.";
  if (!/^[a-z0-9]/.test(name)) return "File names must start with a lowercase letter or number.";
  if (!/^[a-z0-9._-]+$/.test(name)) {
    return "Use only lowercase letters, numbers, dots, underscores or hyphens (no spaces, capitals or symbols).";
  }
  return null;
}

export interface UploadResult {
  path: string;
  fileName: string;
  sizeKb: number;
  assetType: AssetType;
  dimensions: string | null;
}

function readImageDimensions(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve(`${img.naturalWidth} × ${img.naturalHeight}`);
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
  });
}

/**
 * Uploads a single file to Readdy Backend Storage and returns the metadata needed
 * to record it in the media library. Throws a friendly Error on validation or
 * upload failure.
 */
export async function uploadMediaFile(file: File): Promise<UploadResult> {
  const nameError = validateFileName(file.name);
  if (nameError) throw new Error(`${file.name} — ${nameError}`);

  if (file.size > MAX_KB * 1024) {
    throw new Error(`${file.name} is larger than ${Math.round(MAX_KB / 1024)} MB.`);
  }

  const stamp = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 6);
  const base = file.name.replace(/\.[^.]+$/, "").slice(0, 40);
  const ext = extensionOf(file.name) || "bin";
  const objectName = `${base}-${stamp}-${rand}.${ext}`;
  const path = `${UPLOAD_DIR}/${objectName}`;

  const { error } = await supabase.storage.from(MEDIA_BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;

  const assetType = assetTypeFor(file);
  const dimensions = assetType === "image" ? await readImageDimensions(file) : null;

  return {
    path,
    fileName: file.name,
    sizeKb: Math.max(1, Math.round(file.size / 1024)),
    assetType,
    dimensions,
  };
}

/**
 * Media rows store either an external https URL or a Readdy Backend Storage object
 * path. External URLs are used directly; storage paths are resolved to short-lived
 * signed URLs at runtime.
 */
export function isStoragePath(url: string | null | undefined): boolean {
  return Boolean(url) && !/^https?:\/\//i.test(url as string);
}

export async function resolveAssetUrls(paths: string[]): Promise<Record<string, string>> {
  const unique = Array.from(new Set(paths.filter((path) => isStoragePath(path))));
  if (unique.length === 0) return {};

  const { data, error } = await supabase.storage.from(MEDIA_BUCKET).createSignedUrls(unique, 3600);
  if (error || !data) return {};

  const map: Record<string, string> = {};
  data.forEach((entry) => {
    if (entry.path && entry.signedUrl) map[entry.path] = entry.signedUrl;
  });
  return map;
}